import { useCallback } from 'react'
import { Box, IconButton } from '@mui/material'
import { Settings } from '@mui/icons-material'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { LocalizationProvider, DatePicker, CalendarPickerSkeleton } from '@mui/lab'
import { setAccessMonitoringVehicleFilter } from '../../api/dataSlice'
import { useAccessFooterState } from './useAccessFooterState'
import DayjsAdapter from '@mui/lab/AdapterDayjs'
import i18next from 'i18next'
import dayjs from 'dayjs'
import TimeSelector from '../controls/TimeSelector'

const overlaySx = {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none"
}

/**
 * Mobile-portrait footer peek for the Access Monitoring sub-view.
 * Shows a day-only date selector (with step-left/right arrows) plus a Settings icon that opens
 * the full controls drawer. Owns its own `useAccessFooterState` — it only mounts when access
 * monitoring is the active mysiteView.
 *
 * @param {object} props
 * @param {boolean} props.pickerOpen           controlled open state of the MUI DatePicker
 * @param {Function} props.setPickerOpen        setter for the above
 * @param {Function} props.onOpenDrawer         called when the user taps the Settings gear
 */
export default function AccessFooter({ pickerOpen, setPickerOpen, onOpenDrawer }) {

    const { t } = useTranslation()
    const { isPortrait } = useMobileOrientation()
    
    const dispatch = useDispatch()
    const accessFooterState = useAccessFooterState(true)

    const stepLeft = useCallback(() => {
        if (!accessFooterState.canStepLeft) return
        dispatch(setAccessMonitoringVehicleFilter(null))
        accessFooterState.stepLeft()
    }, [accessFooterState, dispatch])

    const stepRight = useCallback(() => {
        if (!accessFooterState.canStepRight) return
        dispatch(setAccessMonitoringVehicleFilter(null))
        accessFooterState.stepRight()
    }, [accessFooterState, dispatch])

    const onDateChange = useCallback((value) => {
        if (!value) return
        dispatch(setAccessMonitoringVehicleFilter(null))
        accessFooterState.updateTimestamp(dayjs(value).format('YYYY-MM-DD'))
    }, [accessFooterState, dispatch])

    // this component is only rendered on mobile-portrait; bail out if mounted elsewhere
    if (!isMobile || !isPortrait) return null

    return (
        <Box sx={{
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center"
        }}>
            <LocalizationProvider dateAdapter={DayjsAdapter} locale={i18next.language}>
                <DatePicker
                    label={t("date.date")}
                    inputFormat={t("date.format.full")}
                    mask={t("date.mask.full")}
                    renderInput={({ inputRef }) => (
                        <TimeSelector
                            stepLeft={stepLeft}
                            timestamp={accessFooterState.effectiveTimestamp}
                            stepRight={stepRight}
                            showDate={true}
                            showTime={false}
                            canStepLeft={accessFooterState.canStepLeft}
                            canStepRight={accessFooterState.canStepRight}
                            loading={accessFooterState.loading}
                            setPickerOpen={setPickerOpen}
                            inputRef={inputRef}
                        />
                    )}
                    value={accessFooterState.effectiveTimestamp}
                    onChange={onDateChange}
                    cancelText={t("actions.cancel")}
                    minDate={accessFooterState.minDate}
                    maxDate={accessFooterState.maxDate}
                    loading={accessFooterState.loading}
                    shouldDisableDate={accessFooterState.shouldDisableDate}
                    renderLoading={() => <CalendarPickerSkeleton />}
                    showTodayButton
                    todayText={t("date.today")}
                    open={pickerOpen}
                    onClose={() => setPickerOpen(false)}
                />
            </LocalizationProvider>

            <Box sx={overlaySx}>
                <IconButton
                    sx={{ p: 1, pointerEvents: "all" }}
                    onClick={onOpenDrawer}
                    color="primary"
                    disabled={accessFooterState.loading}
                >
                    <Settings sx={{ fontSize: "1.66rem" }} />
                </IconButton>
            </Box>
        </Box>
    )
}
